import {
  useMediaQuery,
  WhmBox,
  WhmButton,
  WhmDialog,
  WhmDialogActions,
  WhmDialogContent,
  WhmDialogTitle,
  WhmDivider,
  WhmIconButton,
  WhmLoadingButton,
  WhmMenu,
  WhmMenuItem,
  WhmSkeleton,
  WhmStack,
  whmTheme,
  WhmTooltip,
  WhmTypography,
} from "@securitize/reactjs-whm";
import {
  useDeleteInvestment,
  useGetInProgressInvestments,
} from "../../../../../../data/usePortfolio";
import { ErrorPage } from "../../../../../shared/ErrorPage";
import InProgressEmptyState from "./InProgressEmptyState";
import StyledCard from "../../../StyledCard";
import TokenAvatar from "../../TokenAvatar";
import { Translate } from "../../../../../Translation/Translate";
import {
  I18nContext,
  translationKeys,
} from "../../../../../../contexts/I18nContext";
import { useContext, useMemo, useState } from "react";
import { CurrencyFormat } from "@securitize/reactjs-sid-utils";
import { ReactComponent as Dots } from "@securitize/assets-sec-ui/dist/icons/svg/more-options.svg";
import { InProgressInvestment } from "../../../../../../data/dtos/get-in-progress-assets.dto";
import useUserInfo from "../../../../../../hooks/useUserInfo";
import { RedirectAuthenticatedContext } from "../../../../../../contexts/RedirectAuthenticatedContext";
import { useNavigate } from "react-router";
import { SnackbarContext } from "../../../../../shared/Snackbar";
import ErrorSnackbar from "../../../../../shared/ErrorSnackbar";
import { useQueryClient } from "@tanstack/react-query";
import { ReactComponent as CheckIcon } from "@securitize/assets-sec-ui/dist/icons/svg/check.svg";

function InProgressCard({
  id,
  tokenIcon,
  issuerLogo,
  blockchainNetworkIconUrl,
  tokenName,
  pledged,
  totalFunded,
  subscriptionAgreementStatus,
  isPrimary,
  issuerName,
  opportunityId,
  dashboardUrl,
}: InProgressInvestment) {
  const user = useUserInfo();
  const isMobile = useMediaQuery(whmTheme.breakpoints.down("xl"));
  const cancelInvestmentMutation = useDeleteInvestment();
  const redirectContext = useContext(RedirectAuthenticatedContext);
  const navigate = useNavigate();
  const snackbarContext = useContext(SnackbarContext);
  const { getTranslation } = useContext(I18nContext);
  const queryClient = useQueryClient();
  const isEditor = user.role === "editor";

  const pledgedFormat = useMemo(
    () =>
      new CurrencyFormat({
        currency: pledged.currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 5,
      }),
    [pledged.currency],
  );

  const fundedFormat = useMemo(
    () =>
      new CurrencyFormat({
        currency: totalFunded.currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 5,
      }),
    [totalFunded.currency],
  );

  const [menuOpen, setMenuOpen] = useState(false);
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const hideMoreActions =
    totalFunded.amount > 0 ||
    user.role !== "admin" ||
    subscriptionAgreementStatus === "CONFIRMED";

  const goToInvestment = () => {
    if (isEditor) return;

    if (isPrimary) {
      navigate(`/primary-market/opportunities/${opportunityId!}`);
    } else {
      const url = new URL(dashboardUrl!);
      url.hash = `/opportunity/${opportunityId!}`;

      redirectContext.onRedirect({
        backUrl: url.toString(),
        issuer: {
          name: issuerName,
          logo: issuerLogo,
        },
      });
    }
  };

  return (
    <>
      <StyledCard onClick={goToInvestment}>
        {isMobile ? (
          <WhmStack gap="16px">
            <WhmStack direction="row" gap="16px" alignItems="center">
              <WhmStack direction="row" gap="8px" flex="1" alignItems="center">
                <TokenAvatar
                  tokenIcon={tokenIcon}
                  issuerLogo={issuerLogo}
                  networkIcon={blockchainNetworkIconUrl}
                />
                <WhmTooltip title={tokenName}>
                  <WhmTypography
                    fontWeight={500}
                    sx={{
                      overflow: "hidden",
                      display: "-webkit-box",
                      WebkitLineClamp: "2",
                      WebkitBoxOrient: "vertical",
                    }}
                  >
                    {tokenName}
                  </WhmTypography>
                </WhmTooltip>
              </WhmStack>

              {!hideMoreActions && (
                <WhmIconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setMenuOpen(true);
                    setAnchor(e.currentTarget);
                  }}
                >
                  <Dots fill={whmTheme.palette.action.active} />
                </WhmIconButton>
              )}
            </WhmStack>
            <WhmStack direction="row" justifyContent="space-between">
              <WhmTypography variant="body1" color="text.secondary">
                <Translate
                  translationKey={translationKeys.PORTFOLIO_IN_PROGESS_PLEDGED}
                />
              </WhmTypography>
              <WhmTypography textAlign="right">
                {pledgedFormat.format(pledged.amount)}
              </WhmTypography>
            </WhmStack>
            <WhmStack direction="row" justifyContent="space-between">
              <WhmTypography variant="body1" color="text.secondary">
                <Translate
                  translationKey={translationKeys.PORTFOLIO_IN_PROGESS_FUNDED}
                />
              </WhmTypography>
              <WhmTypography textAlign="right">
                {fundedFormat.format(totalFunded.amount)}
              </WhmTypography>
            </WhmStack>
            {!isEditor && (
              <WhmButton fullWidth variant="contained" onClick={goToInvestment}>
                <Translate
                  translationKey={
                    translationKeys.PORTFOLIO_IN_PROGESS_COMPLETE_CTA
                  }
                />
              </WhmButton>
            )}
          </WhmStack>
        ) : (
          <WhmStack
            direction="row"
            gap="16px"
            alignItems="center"
            justifyContent="space-between"
          >
            <WhmStack
              direction="row"
              gap="16px"
              alignItems="center"
              flexGrow="1"
            >
              <WhmStack
                direction="row"
                gap="8px"
                width={{ xl: "226px", xxl: "256px" }}
                alignItems="center"
              >
                <TokenAvatar
                  tokenIcon={tokenIcon}
                  issuerLogo={issuerLogo}
                  networkIcon={blockchainNetworkIconUrl}
                />
                <WhmTooltip title={tokenName}>
                  <WhmTypography
                    fontWeight={500}
                    sx={{
                      overflow: "hidden",
                      display: "-webkit-box",
                      WebkitLineClamp: "2",
                      WebkitBoxOrient: "vertical",
                    }}
                  >
                    {tokenName}
                  </WhmTypography>
                </WhmTooltip>
              </WhmStack>
              <WhmStack
                direction="row"
                gap="24px"
                alignItems="center"
                minWidth="256px"
              >
                <WhmStack flex="1">
                  <WhmTypography variant="body1" color="text.secondary">
                    <Translate
                      translationKey={
                        translationKeys.PORTFOLIO_IN_PROGESS_PLEDGED
                      }
                    />
                  </WhmTypography>
                  <WhmTypography>
                    {pledgedFormat.format(pledged.amount)}
                  </WhmTypography>
                </WhmStack>
                <WhmStack flex="1">
                  <WhmTypography variant="body1" color="text.secondary">
                    <Translate
                      translationKey={
                        translationKeys.PORTFOLIO_IN_PROGESS_FUNDED
                      }
                    />
                  </WhmTypography>
                  <WhmTypography>
                    {fundedFormat.format(totalFunded.amount)}
                  </WhmTypography>
                </WhmStack>
              </WhmStack>
            </WhmStack>
            <WhmStack direction="row" gap="16px" alignItems="center">
              {!isEditor && (
                <WhmButton variant="contained" onClick={goToInvestment}>
                  <Translate
                    translationKey={
                      translationKeys.PORTFOLIO_IN_PROGESS_COMPLETE_CTA
                    }
                  />
                </WhmButton>
              )}

              <WhmBox>
                <WhmIconButton
                  size="small"
                  sx={{ visibility: hideMoreActions ? "hidden" : undefined }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setMenuOpen(true);
                    setAnchor(e.currentTarget);
                  }}
                >
                  <Dots fill={whmTheme.palette.action.active} />
                </WhmIconButton>
              </WhmBox>
            </WhmStack>
          </WhmStack>
        )}
      </StyledCard>

      <WhmMenu
        open={menuOpen}
        anchorEl={anchor}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        onClose={() => {
          setMenuOpen(false);
          setAnchor(null);
        }}
      >
        <WhmMenuItem
          onClick={() => {
            setMenuOpen(false);
            goToInvestment();
          }}
        >
          <Translate
            translationKey={
              translationKeys.PORTFOLIO_IN_PROGESS_VIEW_INVESTMENT
            }
          />
        </WhmMenuItem>
        <WhmDivider />
        <WhmMenuItem
          onClick={() => {
            setMenuOpen(false);
            setShowDeleteModal(true);
          }}
        >
          <WhmTypography color="error">
            <Translate
              translationKey={
                translationKeys.PORTFOLIO_IN_PROGESS_DELETE_INVESTMENT
              }
            />
          </WhmTypography>
        </WhmMenuItem>
      </WhmMenu>
      <WhmDialog
        open={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
        }}
      >
        <WhmDialogTitle>
          <WhmTypography variant="h4">
            <Translate
              translationKey={
                translationKeys.PORTFOLIO_IN_PROGESS_DELETE_INVESTMENT
              }
            />
          </WhmTypography>
        </WhmDialogTitle>
        <WhmDialogContent>
          <Translate
            translationKey={
              translationKeys.PORTFOLIO_IN_PROGESS_DELETE_INVESTMENT_BUTTON_DISCLAIMER
            }
          />
        </WhmDialogContent>
        <WhmDialogActions>
          <WhmButton
            color="secondary"
            variant="outlined"
            onClick={() => {
              setShowDeleteModal(false);
            }}
            disabled={cancelInvestmentMutation.isPending}
          >
            <Translate
              translationKey={
                translationKeys.PORTFOLIO_IN_PROGESS_DELETE_INVESTMENT_BUTTON_CANCEL
              }
            />
          </WhmButton>
          <WhmLoadingButton
            sx={{
              svg: {
                display: "block !important",
              },
              "&.MuiLoadingButton-loading": {
                color: "black",
                opacity: "0.3",
                span: {
                  color: "black",
                },
              },
            }}
            color="error"
            variant="contained"
            onClick={() => {
              cancelInvestmentMutation.mutate(id, {
                onSuccess: (data) => {
                  setShowDeleteModal(false);
                  queryClient.setQueryData(["in-progress"], data);
                  snackbarContext.showSnackbar({
                    message: (
                      <WhmStack direction="row" alignItems="center" gap="8px">
                        <CheckIcon fill="white" />
                        {getTranslation(
                          translationKeys.PORTFOLIO_IN_PROGESS_DELETE_INVESTMENT_SUCCESS,
                        )}
                      </WhmStack>
                    ),
                    severity: "info",
                  });
                },
                onError: (error) => {
                  snackbarContext.showSnackbar({
                    message: <ErrorSnackbar error={error} />,
                    severity: "error",
                  });
                },
              });
            }}
            loading={cancelInvestmentMutation.isPending}
          >
            {cancelInvestmentMutation.isPending ? (
              <Translate
                translationKey={
                  translationKeys.PORTFOLIO_IN_PROGESS_DELETE_INVESTMENT_BUTTON_PENDING
                }
              />
            ) : (
              <Translate
                translationKey={
                  translationKeys.PORTFOLIO_IN_PROGESS_DELETE_INVESTMENT_BUTTON
                }
              />
            )}
          </WhmLoadingButton>
        </WhmDialogActions>
      </WhmDialog>
    </>
  );
}

function InProgressSkeleton() {
  const isMobile = useMediaQuery(whmTheme.breakpoints.down("xl"));

  if (isMobile) {
    return (
      <WhmStack gap="16px">
        {new Array(3).fill(0).map((_, i) => (
          <StyledCard key={i}>
            <WhmStack gap="16px">
              <WhmStack direction="row" gap="16px">
                <WhmStack
                  direction="row"
                  gap="8px"
                  flex="1"
                  alignItems="center"
                >
                  <WhmSkeleton variant="circular" height="32px" width="32px" />
                  <WhmSkeleton
                    variant="rounded"
                    height="32px"
                    sx={{ flex: 1 }}
                  />
                </WhmStack>

                <WhmSkeleton variant="rounded" height="32px" width="32px" />
              </WhmStack>
              <WhmSkeleton variant="rounded" height="24px" />
              <WhmSkeleton variant="rounded" height="24px" />
              <WhmSkeleton variant="rounded" height="42px" />
            </WhmStack>
          </StyledCard>
        ))}
      </WhmStack>
    );
  } else {
    return (
      <WhmStack gap="16px">
        {new Array(3).fill(0).map((_, i) => (
          <StyledCard key={i}>
            <WhmStack direction="row" gap="16px">
              <WhmStack direction="row" gap="8px" flex="1" alignItems="center">
                <WhmSkeleton variant="circular" height="32px" width="32px" />
                <WhmSkeleton variant="rounded" height="40px" sx={{ flex: 1 }} />
              </WhmStack>
              <WhmSkeleton variant="rounded" height="40px" sx={{ flex: 1 }} />
              <WhmSkeleton variant="rounded" height="40px" sx={{ flex: 1 }} />
              <WhmSkeleton variant="rounded" height="40px" sx={{ flex: 1 }} />
            </WhmStack>
          </StyledCard>
        ))}
      </WhmStack>
    );
  }
}

export default function InProgress() {
  const inProgressQuery = useGetInProgressInvestments();

  if (inProgressQuery.isPending) {
    return <InProgressSkeleton />;
  }

  if (inProgressQuery.isError) {
    return (
      <ErrorPage
        errorCode={inProgressQuery.error.correlationId}
        onRefresh={() => inProgressQuery.refetch()}
      />
    );
  }

  if (inProgressQuery.data.inProgress.length === 0) {
    return <InProgressEmptyState />;
  }

  return (
    <WhmStack gap="16px">
      {inProgressQuery.data.inProgress.map((investment) => (
        <InProgressCard key={investment.tokenId} {...investment} />
      ))}
    </WhmStack>
  );
}
